<script setup lang="ts">
import { computed } from 'vue'
import FilterPanel from '@/browse/FilterPanel.vue'
import FilterSection from '@/browse/FilterSection.vue'
import SelectFacet from '@/browse/SelectFacet.vue'
import RadioFacet from '@/browse/RadioFacet.vue'
import { facetOptions } from '@/browse/browse_state'
import type { BrowseState } from '@/browse/browse_state'
import { selectedCategoryOf } from '@/browse/presentlevels/present_levels_browse'
import type { Skill } from '@/presentlevels/no-ai/skill_statement_types'

export type EnrolledGrade = { name: string, val: string }
export type AreaOfNeed = { label: string, value: string }

const props = defineProps<{
  state: BrowseState
  grades: EnrolledGrade[]
  subjects: AreaOfNeed[]
  categories: Skill[]
}>()

const emit = defineEmits<{ refine: [key: string, values: string[]] }>()

const refinements = computed(() => props.state.refinements)

const gradeOptions = computed(() => facetOptions(
  props.grades.map(({ name, val }) => ({ value: val, label: name })),
  undefined,
  refinements.value.grade
))

const subjectOptions = computed(() => facetOptions(props.subjects, undefined, refinements.value.subject))

const categoryOptions = computed(() => facetOptions(
  props.categories.map(({ skill_uuid, skill_name, locked }) => ({ value: skill_uuid, label: skill_name, isLocked: Boolean(locked) })),
  undefined,
  refinements.value.category
))

function selectGrade(grade: string) {
  emit('refine', 'grade', [grade])
}

function selectSubject(subject: string) {
  emit('refine', 'subject', [subject])
}

// Choosing the shown category again keeps it, as a radio does, rather than clearing it.
function selectCategory(categoryUuid: string) {
  const isAlreadyShown = categoryUuid === selectedCategoryOf(props.state)
  if (isAlreadyShown) {
    return
  }
  emit('refine', 'category', [categoryUuid])
}
</script>

<template>
  <FilterPanel>
    <FilterSection v-slot="{ labelledBy }" heading="Student's Grade">
      <SelectFacet :options="gradeOptions" :labelled-by="labelledBy" empty-text="Choose a grade" :clearable="false" @select="selectGrade" />
    </FilterSection>

    <FilterSection v-slot="{ labelledBy }" heading="Subjects">
      <SelectFacet :options="subjectOptions" :labelled-by="labelledBy" empty-text="Choose a subject" :clearable="false" @select="selectSubject" />
    </FilterSection>

    <FilterSection v-if="categories.length" v-slot="{ labelledBy }" heading="Categories">
      <RadioFacet :options="categoryOptions" :labelled-by="labelledBy" theme="purple" @toggle="selectCategory" />
    </FilterSection>
  </FilterPanel>
</template>
